import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Upload, CheckCircle2, Image, X } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../utils/api';

const MilestoneCompletionModal = ({ open, onClose, milestone, onSuccess }) => {
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [remarks, setRemarks] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        toast.error('Photo size must be less than 10MB');
        return;
      }
      setPhoto(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const removePhoto = () => {
    setPhoto(null);
    setPhotoPreview(null);
  };

  const handleSubmit = async () => {
    if (!photo) {
      toast.error('Please upload a completion photo');
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('photo', photo);
      formData.append('remarks', remarks || '');

      await api.post(`/milestones/${milestone.milestone_id}/submit-completion`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Milestone completion submitted for admin review');
      setPhoto(null);
      setPhotoPreview(null);
      setRemarks('');
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit completion');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setPhoto(null);
    setPhotoPreview(null);
    setRemarks('');
    onClose();
  };

  if (!milestone) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md bg-white border-0 shadow-2xl rounded-lg">
        <DialogHeader className="pb-4 border-b border-stone-100">
          <DialogTitle className="text-xl font-semibold text-stone-900">
            Mark Milestone Complete
          </DialogTitle>
          <DialogDescription className="text-stone-500">
            {milestone.name}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {/* Milestone Info */}
          <div className="bg-stone-50 rounded-lg p-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-stone-500">Weightage</span>
                <p className="font-semibold text-stone-900">{milestone.weightage}%</p>
              </div>
              <div>
                <span className="text-stone-500">Target Date</span>
                <p className="font-semibold text-stone-900">
                  {new Date(milestone.end_date).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>

          {/* Photo Upload */}
          <div className="space-y-2">
            <Label className="text-sm font-medium text-stone-700">
              Completion Photo <span className="text-red-500">*</span>
            </Label>
            
            {photoPreview ? (
              <div className="relative">
                <img 
                  src={photoPreview} 
                  alt="Completion preview" 
                  className="w-full h-48 object-cover rounded-lg"
                />
                <button
                  onClick={removePhoto}
                  className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-stone-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition-colors">
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <Upload className="w-10 h-10 text-stone-400 mb-3" />
                  <p className="mb-2 text-sm text-stone-500">
                    <span className="font-semibold">Click to upload</span> or drag and drop
                  </p>
                  <p className="text-xs text-stone-400">PNG, JPG up to 10MB</p>
                </div>
                <input 
                  type="file" 
                  className="hidden" 
                  accept="image/*"
                  onChange={handlePhotoChange}
                />
              </label>
            )}
          </div>

          {/* Remarks */}
          <div className="space-y-2">
            <Label className="text-sm font-medium text-stone-700">
              Remarks / Notes
            </Label>
            <Textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Add any notes about the completion..."
              className="min-h-[80px] border-stone-200 rounded-lg resize-none"
            />
          </div>

          {/* Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs text-blue-800">
              This will be submitted for admin review. Once approved, the milestone will be marked complete 
              and visible to the customer.
            </p>
          </div>

          {/* Actions */}
          <div className="flex space-x-3 pt-4 border-t border-stone-100">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="flex-1 h-11 border-stone-200 text-stone-700 hover:bg-stone-50 rounded-lg"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={submitting || !photo}
              className="flex-1 h-11 bg-green-600 hover:bg-green-700 text-white rounded-lg disabled:opacity-50"
            >
              {submitting ? (
                <span className="flex items-center">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Submitting...
                </span>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Submit Completion
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MilestoneCompletionModal;
